<div class="entity-compliance-answers-container">
  <!-- Loading State -->
  <div v-if="is_loading_overview" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-2">Loading compliance data...</p>
  </div>

  <!-- No Data State -->
  <div v-else-if="!has_data" class="text-center py-4">
    <p>No compliance rules assigned to this entity.</p>
  </div>

  <!-- Main Content -->
  <div v-else>
    <!-- Header Summary -->
    <div class="border border-light border-1 rounded-lg mb-3 p-3 shadow bg-colormix">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <p class="text-x fw-bold mb-0">Compliance Progress</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span class="status-badge text-xs manually-verified">{{ total_rules }} Rules</span>
          <button
            class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
            @click="sync_answers"
            :disabled="is_syncing"
          >
            <span v-if="is_syncing" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="fa-solid fa-sync-alt me-1"></i>
            Sync Answers
          </button>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="progress-bar-container">
        <div class="progress-bar-fill" :class="get_progress_color_class(completion_percentage)"></div>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <div class="d-flex gap-3">
          <span class="text-xxs fw-bold">
            <i class="bi bi-check-circle-fill text-success me-1"></i>
            {{ total_answered }} Answered
          </span>
          <span class="text-xxs fw-bold">
            {{ total_unanswered }} Pending
          </span>
        </div>
        <span class="text-xxs fw-bold text-end">{{ completion_percentage }}% Complete</span>
      </div>
    </div>

    <!-- Search -->
    <div class="mb-3 d-none">
      <input
        type="text"
        class="form-control form-control-sm text-xxs rounded"
        placeholder="Search rules..."
        v-model="search_query"
      />
    </div>

    <!-- Rules List -->
    <div v-if="!filtered_rules.length" class="text-center py-4">
      <p>No rules match your search.</p>
    </div>

    <div v-else v-for="rule_item in filtered_rules" :key="rule_item.id" class="mb-2">
      <div class="card border border-dark border-1 rounded-lg p-3 shadow">
        <!-- Rule Header -->
        <div class="section-header text-xs fw-bold border-bottom border-2 pb-2 mb-3">
          <div
            class="rule-header-row"
            @click="toggle_rule_expand(rule_item.id)"
          >
            <!-- Left: Icon + Title -->
            <div class="rule-header-left">
              <span class="rule-status-icon" :class="rule_item.answered_count === rule_item.total_questions ? 'text-success' : 'text-danger'">
                <i :class="rule_item.answered_count === rule_item.total_questions ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
              </span>
              <div class="rule-title-block">
                <span class="text-capitalize text-x fw-bold">{{ rule_item.name }}</span>
                <p class="text-xxs text-muted mb-0" v-if="rule_item.description">
                  {{ truncate_text(rule_item.description, 80) }}
                </p>
              </div>
            </div>

            <!-- Right: Badge + Chevron -->
            <div class="rule-header-right">
              <span
                class="status-badge text-xs"
                :class="rule_item.answered_count === rule_item.total_questions ? 'manually-verified' : 'pending'"
              >
                {{ rule_item.answered_count }}/{{ rule_item.total_questions }}
              </span>
              <i class="chevron-icon" :class="is_rule_expanded(rule_item.id) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
            </div>
          </div>

          <!-- Progress Preview Section (always visible) -->
          <div class="mt-3 pt-2 border-top">
            <div class="p-2 rounded bg-light">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="text-xs text-dark">
                  {{ rule_item.answered_count }} of {{ rule_item.total_questions }} questions answered
                </span>
              </div>

              <!-- Mini progress bar -->
              <div class="progress-bar-container">
                <div
                  class="progress-bar-fill"
                  :class="get_progress_color_class(rule_item.total_questions > 0 ? (rule_item.answered_count / rule_item.total_questions * 100) : 0)"
                  :style="get_progress_bar_width_style(rule_item.answered_count, rule_item.total_questions)"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rule Body (Expanded) -->
        <div v-if="is_rule_expanded(rule_item.id)">
          <!-- Loading Details -->
          <div v-if="is_rule_loading(rule_item.id)" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-xxs mt-2">Loading questions...</p>
          </div>

          <!-- Rule Details -->
          <div v-else-if="get_rule_details(rule_item.id)">
            <!-- Sub-Sections -->
            <div class="sub-sections-container">
              <div
                v-for="sub_section in get_all_sub_sections_for_rule(rule_item.id)"
                :key="`${rule_item.id}-${sub_section.section_id}`"
                class="mb-3"
              >
                <!-- Sub-Section Collapsible Header -->
                <div class="sub-section-card border border-1 rounded-lg shadow-sm">
                  <div
                    class="sub-section-header d-flex justify-content-between align-items-center p-2 border-bottom"
                    @click="toggle_sub_section_collapse(`${rule_item.id}-${sub_section.section_id}`)"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-primary">
                        <i :class="is_sub_section_collapsed(`${rule_item.id}-${sub_section.section_id}`) ? 'bi bi-chevron-right' : 'bi bi-chevron-down'"></i>
                      </span>
                      <span class="text-xs fw-bold">{{ sub_section.section_name }}</span>
                    </div>
                    <span class="badge bg-light text-dark border text-xxs">{{ sub_section.questions.length }} questions</span>
                  </div>

                  <!-- Questions -->
                  <div
                    v-show="!is_sub_section_collapsed(`${rule_item.id}-${sub_section.section_id}`)"
                    class="p-3"
                  >
                    <div
                      v-for="(question, question_index) in sub_section.questions"
                      :key="question.section_question_id"
                      class="question-row mb-3"
                    >
                      <!-- Question Content -->
                      <div class="question-content">

                        <!-- Question text + status badge on the same line -->
                        <div class="question-label-row">
                          <span class="text-dark text-xs fw-7">
                            {{ question_index + 1 }}. {{ question.question_text }}
                          </span>

                          <!-- Status badge: border-only, icon reflects the state -->
                          <span
                            class="status-pill text-xxs"
                            :class="get_answer_source_badge_class(question.answer_source)"
                          >
                            <i 
                              v-if="get_answer_source_icon(question.answer_source)" 
                              :class="get_answer_source_icon(question.answer_source)"
                            ></i>
                            <span>{{ get_answer_source_label(question.answer_source) }}</span>
                          </span>

                          <!-- Date pushed to far right -->
                          <div v-if="question.date_answered" class="question-date">
                            <i class="bi bi-calendar-alt"></i>
                            <span> Date Submitted: {{ format_datetime(question.date_answered) }}</span>
                          </div>
                        </div>

                        <!-- Required badge: only when mandatory AND unanswered -->
                        <span v-if="question.is_mandatory && !question.answer" class="badge-required text-xxs">
                          <i class="bi bi-exclamation-circle-fill"></i>Required
                        </span>

                        <!-- Answer Display -->
                        <div v-if="question.answer" class="mt-2">
                          <!-- FILE TYPE -->
                          <div v-if="is_file_type(question.question_type_raw)" class="file-answer-row">
                            <span class="text-xxs text-muted fw-6">
                              {{ get_file_name_from_url(question.answer) }}
                            </span>
                            <button
                              class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
                              @click.stop="open_file_preview(question.answer, get_file_name_from_url(question.answer))"
                            >
                              Preview file
                            </button>
                          </div>

                          <!-- MULTISELECT TYPE -->
                          <div v-else-if="is_multiselect_type(question.question_type_raw)" class="multiselect-answer">
                            <div class="d-flex flex-wrap gap-2">
                              <div
                                v-for="choice in question.multiselect_choices"
                                :key="choice"
                                class="form-check"
                              >
                                <input
                                  type="checkbox"
                                  class="form-check-input"
                                  :checked="is_choice_selected(choice, question.selected_answers)"
                                  disabled
                                />
                                <label class="form-check-label text-xxs">{{ choice }}</label>
                              </div>
            </div>
                          </div>

                          <!-- TEXT ANSWER -->
                          <div v-else class="text-xxs text-muted fst-italic answer-text">
                           Answer: {{ question.answer }}
                          </div>
                        </div>

                        <!-- No answer provided -->
                        <div v-else class="text-xxs text-danger mt-2">
                          No answer provided
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!get_all_sub_sections_for_rule(rule_item.id).length" class="text-center py-4">
                <p class="text-muted text-xxs">No sections found.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- File Preview Modal -->
  <div v-if="show_file_preview" @click.self="close_file_preview">
    <FilePreviewComponent
      :presigned_url="preview_file_url"
      :file_name="preview_file_name"
      @close="close_file_preview"
      :key="preview_file_url"
    />
  </div>
</div>